import { Body, Box, Button, Flex, Icon } from '@hover/blueprint';
import { iSliders } from '@hover/icons';
import React from 'react';

import { getNumActiveFieldsInFilter } from 'src/modules/designStudio/components/DesignMenu/ProductCatalogMenu/ProductSearch/FilteredSearchResults/utils';
import { TestIds } from 'src/modules/designStudio/components/DesignMenu/ProductCatalogMenu/ProductSearch/TestIds';
import { ColorGroupDisplayHex } from 'src/modules/designStudio/helpers/productSelection/VariationColorGroups';
import type { ProductFilter } from 'src/modules/designStudio/helpers/productSelection/types';
import { TranslationNamespace, useTranslate } from 'src/shared/hooks';

export type FilterTabParams = {
  filter: ProductFilter;
  numResults: number;
  onColorButtonClicked: () => void;
  onFilterButtonClicked: () => void;
};

/**
 * @description This component allows the user to do the following:
 * - view the number of products that satisfy the active filter
 * - view the selected color in their filter
 * - open up the filter screen to view all filter options and make modifications
 */
export const FilterTab = ({
  filter,
  onColorButtonClicked,
  onFilterButtonClicked,
  numResults,
}: FilterTabParams) => {
  const t = useTranslate(TranslationNamespace.DESIGN_STUDIO);
  const numFilterFieldsActive = getNumActiveFieldsInFilter(filter);
  return (
    <Flex alignItems="center" direction="row-reverse" gap={200}>
      <Button
        data-testid={TestIds.EDIT_FILTER_BUTTON}
        onClick={onFilterButtonClicked}
        py={300}
        size="small"
        variant="secondary"
      >
        <Flex
          alignItems="center"
          direction="row"
          gap={100}
          justifyContent="center"
        >
          <Icon icon={iSliders} m={0} />
          <Flex
            bg="neutral.900"
            flexGrow={0}
            flexShrink={0}
            fontSize={300}
            fontWeight={700}
            style={{
              width: '18px',
              height: '18px',
              lineHeight: '18px',
              alignItems: 'center',
              justifyContent: 'center',
              borderRadius: '50%',
              textAlign: 'center',
              padding: '3px',
            }}
            textColor="white"
          >
            {numFilterFieldsActive}
          </Flex>
        </Flex>
      </Button>
      <Button
        data-testid={TestIds.EDIT_FILTER_COLOR_BUTTON}
        onClick={onColorButtonClicked}
        py={300}
        size="small"
        variant="secondary"
      >
        <Flex direction="row" gap={100}>
          <Box
            alignContent="center"
            bg={
              filter.colorGroups.length >= 1
                ? ColorGroupDisplayHex[filter.colorGroups[0]]
                : '#000000'
            }
            border="none"
            borderRadius="50%"
            height="16px"
            marginRight="5px"
            marginTop="1px"
            outlineColor="neutral.900"
            outlineOffset="2px"
            sx={{
              outlineStyle: 'solid',
              outlineWidth: '2px',
            }}
            width="16px"
          />
          <Body fontSize={300} fontWeight={700} my={0}>
            {t('productCatalogMenu.productDetails.colors')}
          </Body>
        </Flex>
      </Button>
      <Body
        fontSize={300}
        fontWeight={700}
        height="fit-content"
        marginRight="auto"
        my={0}
      >
        {`${numResults.toLocaleString()} Results`}
      </Body>
    </Flex>
  );
};
